import * as R from 'ramda';
import { normalize, schema } from 'normalizr';
import {
    defaultPrimaryColor,
    defaultSecondaryColor,
} from '../../constants/colors';
import * as Sentry from '@sentry/react';
import sportsApi from '../../utils/sportsApi';
import { authUrl } from '../../utils/apiUrls';
import verifyJwt from './verifyJwt';

const EIGHT_HOURS = 288e5;

const clientsSchema = [new schema.Entity('clients')];

const login =
    store =>
    async (state, { email, password }) => {
        try {
            const loadingState = R.assoc('isLoading', true, state.user);
            store.setState({ user: loadingState });

            let error = null;

            const usersRequest = await sportsApi
                .url('/users')
                .auth(`Basic ${btoa(email + ':' + password)}`)
                .query({ username: email })
                .get()
                .badRequest(e => (error = e))
                .unauthorized(e => {
                    const errorResponse = JSON.parse(e.message);
                    if (typeof errorResponse !== 'object') return;
                    const message = errorResponse.ssoError
                        ? `We are unable to log you in as your account can ` +
                          `only access MVC by connecting through your company’s Single Sign On (SSO) provider.`
                        : `The email and password combination is incorrect`;
                    return (error = {
                        ...errorResponse,
                        message: message,
                    });
                });

            const res = await fetch(`${authUrl}/login`, {
                method: 'GET',
                headers: {
                    authorization: `Basic ${btoa(`${email}:${password}`)}`,
                    accept: '*/*',
                },
                next: {
                    revalidate: 0,
                },
            });

            Sentry.addBreadcrumb({
                category: 'API',
                message: 'logging user in',
                data: {
                    responseCode: res.status,
                    email,
                },
            });

            if (!res.ok) {
                throw new Error(
                    'The email and password combination is incorrect',
                );
            }

            const { JWT } = await res.json();

            if (!JWT) {
                throw new Error(
                    'The email and password combination is incorrect',
                );
            }

            const payload = await verifyJwt(JWT);
            let isAuthenticated = !!payload?.user;
            if (!isAuthenticated) {
                throw new Error('The JWT is invalid');
            }

            // Hacky way of getting a token
            const { headers } = await usersRequest.res();

            if (error) return R.assocPath(['user', 'error'], error, state);

            // Retrieve payload (user information)
            const userList = await usersRequest.json();

            const user = R.head(userList);
            const token = headers.get('x-auth-token');
            let clients = R.path(['clients'], user) || null;

            if (!clients.length)
                return R.assocPath(
                    ['user', 'error'],
                    'You do not have a client associated to your account',
                    state,
                );

            //fix reset colors bug
            clients = clients.map(c => {
                const newClient = c;
                newClient.primaryColor = c.primaryColor
                    ? c.primaryColor
                    : defaultPrimaryColor;
                newClient.secondaryColor = c.secondaryColor
                    ? c.secondaryColor
                    : defaultSecondaryColor;
                return newClient;
            });

            // Generate a timestamp to store token validation time
            user.timestamp = Date.now() + EIGHT_HOURS;

            user.isSelectingClient = true;
            user.token = token;

            const normalizedClients = normalize(user.clients, clientsSchema);
            user.clientsById = normalizedClients.entities.clients;

            store.setState({
                user: {
                    ...store.getState().user,
                    ...user,
                    isLoading: false,
                    error: null,
                    ssoLogin: false,
                    cubeToken: JWT,
                },
            });
        } catch (e) {
            store.setState({
                user: {
                    isLoading: false,
                    error: {
                        message: e.message,
                        ssoError: false,
                    },
                },
            });

            Sentry.addBreadcrumb({
                category: 'API',
                message: 'login error: ' + e.message,
                data: {
                    responseCode: e.status,
                    email,
                },
            });
            // We need to re-throw the error to be caught by the ErrorBoundary
            throw e;
        }
    };

export default login;
